import { WifiOff } from 'lucide-react';
import { ProductLogo } from '@/shared/ui/brand';
import { cn } from '@/lib/utils';

export interface BrandLoaderHeroProps {
  className?: string;
}

/**
 * Branded loader orb — logo in a soft circle with a spinning teal–coral arc ring.
 */
export function BrandLoaderHero({ className }: BrandLoaderHeroProps): JSX.Element {
  return (
    <div className={cn('relative mb-6 inline-flex size-[7.25rem] items-center justify-center', className)}>
      <div className="pointer-events-none absolute inset-0 rounded-full bs-pulse-brand" aria-hidden />
      <div
        className="pointer-events-none absolute inset-0 rounded-full bs-pulse-brand bs-pulse-delay"
        aria-hidden
      />

      <div className="brand-loader-orbit pointer-events-none absolute inset-0" aria-hidden />

      <div className="relative flex size-28 items-center justify-center overflow-hidden rounded-full border border-brand-200/50 bg-gradient-to-br from-white via-brand-50/90 to-brand-100 p-5 shadow-card ring-1 ring-inset ring-white/80">
        <ProductLogo className="size-12 drop-shadow-sm" />
      </div>
    </div>
  );
}

/**
 * Offline hero — same branded logo circle as the loader, with a coral disconnected ring
 * and a small wifi badge (no strike-through over the mark).
 */
export function BrandOfflineHero({ className }: BrandLoaderHeroProps): JSX.Element {
  return (
    <div className={cn('relative mb-6 inline-flex size-[7.25rem] items-center justify-center', className)}>
      <div className="pointer-events-none absolute inset-0 rounded-full bs-pulse-coral" aria-hidden />
      <div
        className="pointer-events-none absolute inset-0 rounded-full bs-pulse-coral bs-pulse-delay"
        aria-hidden
      />

      <div
        className="pointer-events-none absolute inset-0 rounded-full border-2 border-dashed border-coral-500/40"
        aria-hidden
      />

      <div className="relative flex size-28 items-center justify-center overflow-hidden rounded-full border border-brand-200/50 bg-gradient-to-br from-white via-brand-50/90 to-brand-100 p-5 shadow-card ring-1 ring-inset ring-white/80">
        <ProductLogo className="size-12 drop-shadow-sm" />
      </div>

      <div
        className="absolute -right-0.5 -bottom-0.5 flex size-9 items-center justify-center rounded-full border-2 border-white bg-coral-500 text-white shadow-md"
        aria-hidden
      >
        <WifiOff className="size-4" strokeWidth={2.25} />
      </div>
    </div>
  );
}

/** Windows-style three-dot pulse using brand teal + coral. */
export function BrandLoaderDots({ className }: { className?: string }): JSX.Element {
  return (
    <div className={cn('brand-loader-dots', className)} role="presentation" aria-hidden>
      <span className="brand-loader-dot" />
      <span className="brand-loader-dot" />
      <span className="brand-loader-dot" />
    </div>
  );
}
